import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';

import get from 'lodash.get';
import { getHashFragment } from '../../utils/uriUtils';
import classnames from 'classnames';
import styles from './SbSharedRedirectedItem.scss';

const browseBelowMessage = (
    <FormattedMessage
        id="SbSharedRedirectedItem.browseBelow"
        defaultMessage="Browse available items below."
    />
);

const SbSharedRedirectedItemComponent = ({ viewer, relay }) => {
    const { item } = viewer;

    useEffect(() => {
        const { href } = window.location;
        const fragment = getHashFragment(href);
        if (fragment.includes('#itemId')) {
            const itemId = fragment.split('#itemId=')[1];
            if (itemId) {
                relay.refetch({
                    hasItemId: true,
                    itemId,
                });
            }
        }
    }, [relay]);

    if (item) {
        const { photos, title } = item;
        const photo = get(photos, '0.smallPath') || null;
        const textWrapperClass = classnames(styles.textWrapper, {
            [styles.withoutImage]: !photo,
        });

        const titleText = photo ? (
            <FormattedMessage
                id="SbSharedRedirectedItem.notAvailableItemWithPhoto"
                defaultMessage="Sorry, this item is not currently available."
            />
        ) : (
            <FormattedMessage
                id="SbSharedRedirectedItem.notAvailableItemWithoutPhoto"
                defaultMessage="Sorry, {title} is not currently available."
                values={{ title: <span className={styles.itemTitle}>{title}</span> }}
            />
        );

        return (
            <div className={styles.wrapper} data-tn="redirected-item">
                {photo && (
                    <div className={styles.imageWrapper}>
                        <img
                            className={styles.image}
                            src={photo}
                            alt={title}
                            data-tn="redirected-item-image"
                        />
                    </div>
                )}
                <div className={textWrapperClass}>
                    <div className={styles.title}>{titleText}</div>
                    <div className={styles.text}>{browseBelowMessage}</div>
                </div>
            </div>
        );
    }

    return null;
};

SbSharedRedirectedItemComponent.propTypes = {
    viewer: PropTypes.shape({
        item: PropTypes.object,
    }),
    relay: PropTypes.object,
};

export const SbSharedRedirectedItem = createRefetchContainer(
    SbSharedRedirectedItemComponent,
    {
        viewer: graphql`
            fragment SbSharedRedirectedItem_viewer on Viewer
                @argumentDefinitions(
                    itemId: { type: "String", defaultValue: "" }
                    hasItemId: { type: "Boolean", defaultValue: false }
                ) {
                __typename
                item(itemId: $itemId) @include(if: $hasItemId) {
                    title
                    photos {
                        smallPath
                    }
                }
            }
        `,
    },
    graphql`
        query SbSharedRedirectedItemRefetchQuery($itemId: String!, $hasItemId: Boolean!) {
            viewer {
                ...SbSharedRedirectedItem_viewer @arguments(hasItemId: $hasItemId, itemId: $itemId)
            }
        }
    `
);
